'use client';
import pendingLabelStyles from './pending-assistant-label.module.css';
import { useAssistantReplyStarted } from './pending-assistant-label-context';

import { ShiningText } from '@/components/ui/shining-text';
import { CHAT_MESSAGE_TEXT_INSET_X_CLASS } from '@/components/chat/chat-message-layout';
import { cn } from '@/lib/utils';
import { isGenericPendingAssistantLabel } from '@/i18n/pending-assistant-copy';
import { AgentActivityMark } from './agent-activity-mark';
import type { ReactNode } from 'react';
import {
  resolveActivityStatusPresentation,
  type ActivityStatusPhase,
} from './activity-status-presentation';

export function ActivityStatusText({
  phase,
  text,
  className,
  animate = true,
}: {
  phase: ActivityStatusPhase;
  text: string;
  className?: string;
  animate?: boolean;
}) {
  const presentation = resolveActivityStatusPresentation(phase);

  if (animate && presentation.text === 'shimmer') {
    return (
      <span className={className} data-activity-text-mode="shimmer">
        <ShiningText
          text={text}
          className={cn(className, 'text-transparent')}
        />
      </span>
    );
  }

  return (
    <span className={cn(className)} data-activity-text-mode="static">
      {text}
    </span>
  );
}

export function ActivityStatusRow({
  phase,
  leadingVisual,
  activeVisual,
  text,
  trailing,
  className,
  textClassName,
  singleLine = false,
  standalone = false,
}: {
  phase: ActivityStatusPhase;
  leadingVisual?: ReactNode;
  /** An explicit live owner may replace shimmer with an accessible text + mark pair. */
  activeVisual?: ReactNode;
  text: string;
  trailing?: ReactNode;
  className?: string;
  textClassName?: string;
  singleLine?: boolean;
  standalone?: boolean;
}) {
  const replyStarted = useAssistantReplyStarted();
  const presentation = resolveActivityStatusPresentation(phase);
  const isGenericPendingStatus =
    (phase === 'pending' || phase === 'active') &&
    isGenericPendingAssistantLabel(text);
  const liveVisual =
    phase === 'pending' || phase === 'active'
      ? (activeVisual ??
        (isGenericPendingStatus ? (
          <AgentActivityMark
            state={phase === 'pending' ? 'thinking' : 'working'}
            className={standalone ? 'size-[34px]' : 'size-4'}
          />
        ) : undefined))
      : undefined;
  const showLeadingVisual =
    Boolean(liveVisual) ||
    (presentation.leadingVisual === 'visible' && Boolean(leadingVisual));
  const hasTrailing = Boolean(trailing);

  return (
    <div
      data-tool-row
      data-tool-row-layout="custom"
      data-activity-status-row
      className={cn(
        pendingLabelStyles.owner,
        'text-muted-foreground w-full max-w-full min-w-0 items-center gap-2 overflow-hidden text-sm',
        hasTrailing
          ? showLeadingVisual
            ? 'grid grid-cols-[auto_minmax(0,1fr)_auto]'
            : 'grid grid-cols-[minmax(0,1fr)_auto]'
          : 'flex',
        CHAT_MESSAGE_TEXT_INSET_X_CLASS,
        className
      )}
    >
      {showLeadingVisual ? (
        <span
          data-activity-leading-visual
          className="inline-flex min-w-4 shrink-0 items-center justify-start"
        >
          {liveVisual ?? leadingVisual}
        </span>
      ) : null}
      <div
        data-pending-hover-label={
          replyStarted && isGenericPendingStatus ? 'true' : undefined
        }
        className={cn(
          'max-w-full min-w-0 flex-1 overflow-hidden',
          singleLine && 'text-ellipsis whitespace-nowrap'
        )}
      >
        <ActivityStatusText
          phase={phase}
          animate={!liveVisual}
          text={text}
          className={cn(
            'block max-w-full text-sm font-medium',
            singleLine
              ? 'overflow-hidden text-ellipsis whitespace-nowrap'
              : 'break-words',
            textClassName
          )}
        />
      </div>
      {trailing}
    </div>
  );
}
